import checkService from "../../services/checkService";
import leadService from "../../services/leadService";

export default {
  namespaced: true,
  state: {
    elecProducts: [],
    gasProducts: [],
    ranges: [],
    simulation: []
  },
  mutations: {
    setElecProduct(state, value) {
      state.elecProduct = value;
    },
    setGasProduct(state, value) {
      state.gasProduct = value;
    },
    setElecProducts(state, value) {
      state.elecProducts = value;
    },
    setGasProducts(state, value) {
      state.gasProducts = value;
    },
    setPeers(state, value) {
      state.peers = value;
    },
    setRanges(state, value) {
      state.ranges = value;
    },
    setSimulation(state, value) {
      state.simulation = value;
    }
  },
  actions: {
    async getSimulation(context) {
      let simulationData = await context.dispatch("getSimulationData");

      try {
        let response = await checkService.getSimulation(simulationData);

        if (response.electricity !== undefined) {
          context.commit("setElecProducts", response.electricity);
        } else {
          context.commit("setElecProducts", []);
        }

        if (response.gas !== undefined) {
          context.commit("setGasProducts", response.gas);
        } else {
          context.commit("setGasProducts", []);
        }

        context.dispatch("setRanges");
        context.dispatch("setSimulation");
      } catch (error) {
        throw error;
      }
    },

    setRanges(context) {
      let products = context.state.elecProducts.concat(
        context.state.gasProducts
      );

      const rangesName = [...new Set(products.map(x => x.range))];

      let range = {};

      rangesName.forEach(rangeName => {
        let rangeProducts = products.filter(
          product => product.range === rangeName
        );
        range[rangeName.toUpperCase()] = rangeProducts.reduce(
          (objectsByKeyValue, obj) => {
            const value = obj["energyType"];
            objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(
              obj
            );
            return objectsByKeyValue;
          },
          {}
        );
      });
      context.commit("setRanges", range);
    },

    setSimulation(context) {
      let ranges = context.state.ranges;

      let simulation = {};

      Object.keys(ranges).forEach(range => {
        simulation[range] = {
          possibleRange: {
            electricity: ranges[range].electricity || [],
            gas: ranges[range].gas || []
          },
          options: {
            fix: [],
            longer: [],
            green: []
          },
          name: range,
          amounts: {
            annualAmount: 0,
            monthlyAmount: 0
          }
        };

        let newRange = context.getters["rangeByOption"](
          simulation[range],
          "none"
        );
        simulation[range] = newRange;
      });

      context.commit("setSimulation", simulation);
    },

    getSimulationData(context) {
      let consumptionsArray = context.rootState.consumption.consumptions.filter(
        consumption =>
          context.rootState.consumption.meterType.includes(
            consumption.meterType
          ) && consumption.meterType !== "gas"
      );

      let consumptions = consumptionsArray.reduce((acc, it) => {
        if (it.meterType !== "mono") {
          acc[it.meterType] = it.value;
        } else {
          acc["day"] = it.value;
        }
        return acc;
      }, {});

      let simulationData = {
        customerType: context.rootState.contact.isPro ? "pro" : "resi",
        promo: true,
        eco: true,
        postalCode: context.rootState.calculation.zipCode,
        town: context.rootState.calculation.city
      };

      if (context.rootGetters["energy/hasElec"]) {
        simulationData.electricity = {
          powerPv: context.rootState.consumption.hasSolarPanels
            ? context.rootState.consumption.solarPanelsPower
            : null,
          consumption: consumptions,
          products: context.rootState.podium.simulationProducts.electricity
        };
      }

      if (context.rootGetters["energy/hasGas"]) {
        let consumptionGas = context.rootState.consumption.consumptions.find(
          consumption => consumption.meterType === "gas"
        ).value;

        simulationData.gas = {
          consumption: consumptionGas,
          products: context.rootState.podium.simulationProducts.gas
        };
      }

      return simulationData;
    },
    async save(context, ranges) {
      let data = {
        uuid: context.rootState.common.uuid
      };

      try {
        data["simulationResult"] = ranges;

        await leadService.save(data);
      } catch (error) {
        throw error;
      }
    }
  },
  getters: {
    hasFix: state => (range, energy) => {
      return state.ranges[range][energy].filter(
        products => products.contractType === "fix"
      ).length;
    },
    hasDuration: state => (range, energy, duration) => {
      return state.ranges[range][energy].filter(
        products => products.duration === duration
      ).length;
    },
    defaultRange: (state, getters) => range => {
      let ranges = state.ranges;

      let newRange = {
        possibleRange: {
          electricity: ranges[range.name].electricity || [],
          gas: ranges[range.name].gas || []
        },
        options: {
          fix: [],
          longer: [],
          green: []
        },
        name: range.name,
        amounts: {
          annualAmount: 0,
          monthlyAmount: 0
        }
      };

      newRange = getters["rangeByOption"](newRange, "none");

      return newRange;
    },
    rangeByOption: state => (range, option) => {
      let ranges = state.ranges;

      let newRange = {
        possibleRange: range.possibleRange,
        options: {
          fix: [],
          longer: [],
          green: []
        },
        amounts: {
          annualAmountVAT: 0,
          monthlyAmountVAT: 0,
          annualAmount: 0,
          monthlyAmount: 0
        }
      };

      //option: fix|green|longer
      Object.keys(ranges[range.name]).forEach(energy => {
        let products = range.possibleRange[energy];

        // Filter option
        let productsWithOption = products.filter(product => {
          switch (option) {
            case "fix":
              return (
                product.contractType === "fix" &&
                product.contractType !== range[energy].contractType
              );
            case "longer":
              return product.duration > range[energy].duration;
            case "green":
              return (
                product.total.priceEuroVATEco !== undefined &&
                range.options.green.length === 0 &&
                product.contractType === range[energy].contractType &&
                product.duration === range[energy].duration
              );
            default:
              return 0;
          }
        });

        if (productsWithOption.length) {
          products = productsWithOption;
          newRange.options[option].push(energy);
          newRange.possibleRange[energy] = productsWithOption;
        }

        // Get the cheapest product
        let product = products.reduce(
          (min, p) => (p.total.priceEuroVAT < min.total.priceEuroVAT ? p : min),
          products[0]
        );

        // Set range info
        newRange[energy] = product;
        newRange.name = range.name;

        if (option === "green") {
          newRange.amounts.annualAmountVAT +=
            product.total.priceEuroVATEco || product.total.priceEuroVAT;

          newRange.amounts.monthlyAmountVAT +=
            product.total.priceEuroMonthlyVATEco ||
            product.total.priceEuroMonthlyVAT;

          newRange.amounts.annualAmount +=
            product.total.priceEuroEco || product.total.priceEuro;

          newRange.amounts.monthlyAmount +=
            product.total.priceEuroMonthlyEco || product.total.priceEuroMonthly;
        } else {
          newRange.amounts.annualAmountVAT += product.total.priceEuroVAT;
          newRange.amounts.monthlyAmountVAT +=
            product.total.priceEuroMonthlyVAT;
          newRange.amounts.annualAmount += product.total.priceEuro;
          newRange.amounts.monthlyAmount += product.total.priceEuroMonthly;
        }
      });

      return newRange;
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/simulation.js